"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import { ShutdownBody, ShutdownHeader, SHUTDOWN_DATE_LABEL } from "./ShutdownContent";

// Site-wide shutdown notice: full-screen modal on first visit, collapses to a
// floating pill (top-right) once dismissed. Dismissal is a per-viewer
// convenience in localStorage — if storage is unavailable the modal simply
// shows again on the next page load.
const DISMISS_KEY = "ff-shutdown-notice-dismissed";

export default function ShutdownNotice() {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    } catch {}
    setOpen(!dismissed);
    setMounted(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  // Iframe embeds get no chrome; /shutdown already shows the full notice.
  if (!mounted || pathname?.startsWith("/embed") || pathname === "/shutdown") return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed top-[72px] right-4 z-[9998] inline-flex items-center gap-2 rounded-full bg-gray-900 dark:bg-white px-4 py-2 text-xs font-medium text-white dark:text-gray-900 shadow-lg hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
        Shutting down {SHUTDOWN_DATE_LABEL} · Export your wallet
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shutdown-notice-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-white dark:bg-[#0F0F12] text-gray-900 dark:text-white sm:rounded-2xl shadow-2xl"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="sticky top-0 float-right m-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 dark:bg-[#0F0F12]/90 text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#18181D] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="px-6 sm:px-10 pt-10 pb-10">
          <ShutdownHeader titleId="shutdown-notice-title" />
          <ShutdownBody />
        </div>
      </div>
    </div>
  );
}
